import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { DashboardLayout } from "./DashboardLayout";
import { api, fileUrl } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Bell, ChevronDown, ChevronUp, GripVertical, ImagePlus, Loader2, Plus, Save, Send, Trash2 } from "lucide-react";
import { useAdminRealtime } from "../../hooks/useAdminRealtime";

const FIELDS = [
  ["business_name", "Nombre del negocio"],
  ["whatsapp", "WhatsApp (con lada, ej. 529937036279)"],
  ["instagram", "Instagram (sin @)"],
  ["pickup_address", "Dirección de recogida"],
  ["visible_hours", "Horarios visibles"],
];

export default function Configuracion() {
  const [s, setS] = useState(null);
  const [products, setProducts] = useState([]);
  const [openBanner, setOpenBanner] = useState(null);
  const [uploadingBanner, setUploadingBanner] = useState(null);
  const [openSections, setOpenSections] = useState({
    general: true,
    security: false,
    apk: true,
    public: true,
  });
  const { user } = useAuth();
  const load = useCallback(() => api.get("/settings").then((r) => setS(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/products", { params: { all: true } }).then((r) => setProducts(r.data)).catch(() => {}); }, []);
  useAdminRealtime(load, ["settings"]);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setNotification = (k, v) => setS((p) => ({ ...p, notifications: { ...(p.notifications || {}), [k]: v } }));
  const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  const save = async () => {
    const { data } = await api.put("/settings", s);
    setS(data);
    toast.success("Configuración guardada 💛");
  };
  const testNotification = async () => {
    try {
      const { data } = await api.post("/push-test");
      if (data?.sent > 0) {
        toast.success(`Notificación de prueba enviada a ${data.sent} dispositivo${data.sent === 1 ? "" : "s"} 💌`);
      } else {
        toast.warning("No hay dispositivos registrados para notificaciones todavía.");
      }
    } catch {
      toast.error("No se pudo enviar la notificación de prueba.");
    }
  };
  const banners = s?.hero_banners || [];
  const setBanner = (idx, k, v) => {
    setS((prev) => ({
      ...prev,
      hero_banners: (prev.hero_banners || []).map((b, i) => i === idx ? { ...b, [k]: v } : b),
    }));
  };
  const addBanner = () => {
    const p = products.find((x) => x.available) || products[0];
    setS((prev) => ({
      ...prev,
      hero_banners: [
        ...(prev.hero_banners || []),
        {
          id: `banner-${Date.now()}`,
          enabled: true,
          eyebrow: "Nuevo",
          subtitle: "recién salido del horno",
          title: p?.name || "Especial Kelifa",
          price_text: p?.requires_quote ? "Cotización" : (p?.base_price ? `Desde $${p.base_price}` : ""),
          image: p?.images?.[0] || "/assets/tresleches.jpg",
          cta_label: "Pedir ahora",
          product_id: p?.id || "",
        },
      ],
    }));
    setOpenBanner((s?.hero_banners || []).length);
  };
  const removeBanner = (idx) => {
    setS((prev) => ({ ...prev, hero_banners: (prev.hero_banners || []).filter((_, i) => i !== idx) }));
    if (openBanner === idx) setOpenBanner(null);
  };
  const moveBanner = (idx, dir) => {
    const target = idx + dir;
    setS((prev) => {
      const next = [...(prev.hero_banners || [])];
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...prev, hero_banners: next };
    });
    if (target >= 0 && target < banners.length) setOpenBanner(target);
  };
  const uploadBannerImage = async (idx, file) => {
    setUploadingBanner(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBanner(idx, "image", fileUrl(data.path));
      toast.success("Imagen subida");
    } catch {
      toast.error("No se pudo subir la imagen.");
    } finally {
      setUploadingBanner(null);
    }
  };
  const applyProduct = (idx, productId) => {
    const p = products.find((x) => x.id === productId);
    setS((prev) => ({
      ...prev,
      hero_banners: (prev.hero_banners || []).map((b, i) => {
        if (i !== idx) return b;
        if (!p) return { ...b, product_id: "" };
        return {
          ...b,
          product_id: p.id,
          title: b.title || p.name,
          price_text: b.price_text || (p.requires_quote ? "Cotización" : `Desde $${p.base_price}`),
          image: b.image || p.images?.[0] || "",
        };
      }),
    }));
  };

  if (!s) return <DashboardLayout title="Configuración"><p className="text-cafe/50">Cargando…</p></DashboardLayout>;

  return (
    <DashboardLayout title="Configuración">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,0.95fr)_minmax(520px,1.45fr)] gap-5 xl:gap-7 items-start">
        <div className="space-y-5 min-w-0">
          <ConfigCard eyebrow="Ajustes generales" title="Información del negocio" description="Datos que se usan en la página pública, confirmaciones y contacto."
            open={openSections.general} onToggle={() => toggleSection("general")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FIELDS.map(([k, l]) => (
                <ConfigField key={k} label={l}>
                  <input data-testid={`cfg-${k}`} value={s[k] || ""} onChange={(e) => set(k, e.target.value)} className="admin-input" />
                </ConfigField>
              ))}
              <ConfigField label="Costo de envío (MXN)">
                <input data-testid="cfg-delivery" type="number" value={s.delivery_cost || 0} onChange={(e) => set("delivery_cost", parseFloat(e.target.value) || 0)} className="admin-input" />
              </ConfigField>
              <ConfigField label="Mensaje automático de WhatsApp" className="md:col-span-2">
                <textarea data-testid="cfg-automsg" value={s.auto_message || ""} onChange={(e) => set("auto_message", e.target.value)} rows={4} className="admin-input min-h-[118px] resize-y" />
              </ConfigField>
            </div>
            <button onClick={save} data-testid="cfg-save" className="w-full justify-center bg-vino text-crema rounded-full px-6 py-3 text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Guardar configuración</button>
          </ConfigCard>

          <ConfigCard eyebrow="Seguridad" title="Acceso administrativo" compact open={openSections.security} onToggle={() => toggleSection("security")}>
            <div className="rounded-3xl border border-beige bg-crema/70 p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-cafe/40">Sesión activa</p>
              <p className="text-sm text-cafe mt-1 break-all">{user?.email}</p>
              <p className="text-xs text-cafe/45 mt-2">Para cambiar la contraseña, contacta al equipo de soporte.</p>
            </div>
          </ConfigCard>

          <ConfigCard eyebrow="APK" title="Notificaciones del teléfono" description="Controla qué avisos recibe la administradora en la app instalada. Los cambios aplican al guardar."
            open={openSections.apk} onToggle={() => toggleSection("apk")}>
            <div className="space-y-3">
              <NotificationToggle
                title="Nuevo pedido"
                description="Aviso inmediato cada vez que entra un pedido nuevo."
                checked={s.notifications?.new_order !== false}
                onChange={(v) => setNotification("new_order", v)}
              />
              <NotificationToggle
                title="Pedido para hoy"
                description="Una alerta diaria por cada pedido activo programado para hoy."
                checked={s.notifications?.today_orders !== false}
                onChange={(v) => setNotification("today_orders", v)}
              >
                <ConfigField label="Enviar desde">
                  <input type="time" value={s.notifications?.today_orders_time || "08:00"} onChange={(e) => setNotification("today_orders_time", e.target.value)} className="admin-input" />
                </ConfigField>
              </NotificationToggle>
              <NotificationToggle
                title="Pendiente de confirmar"
                description="Si un pedido sigue como “Nuevo” por mucho tiempo, avisa para darle seguimiento."
                checked={s.notifications?.pending_confirmation !== false}
                onChange={(v) => setNotification("pending_confirmation", v)}
              >
                <ConfigField label="Después de cuántas horas">
                  <input type="number" min="1" max="72" value={s.notifications?.pending_confirmation_hours || 3} onChange={(e) => setNotification("pending_confirmation_hours", parseInt(e.target.value) || 1)} className="admin-input" />
                </ConfigField>
              </NotificationToggle>
              <NotificationToggle
                title="Recordatorio de entrega/recogida"
                description="Avisa antes de la hora elegida por el cliente para preparar entrega o pickup."
                checked={s.notifications?.delivery_reminder !== false}
                onChange={(v) => setNotification("delivery_reminder", v)}
              >
                <ConfigField label="Horas antes">
                  <input type="number" min="1" max="48" value={s.notifications?.delivery_reminder_hours || 2} onChange={(e) => setNotification("delivery_reminder_hours", parseInt(e.target.value) || 1)} className="admin-input" />
                </ConfigField>
              </NotificationToggle>
            </div>
            <div className="rounded-3xl border border-rosa/40 bg-rosa/10 p-4">
              <p className="text-sm font-semibold text-vino">Prueba de notificaciones</p>
              <p className="text-xs text-cafe/55 mt-1">
                Úsalo después de instalar el APK y aceptar permisos para confirmar que este teléfono recibe avisos.
              </p>
              <button type="button" onClick={testNotification} data-testid="cfg-push-test"
                className="mt-4 w-full justify-center bg-blancoCalido border-2 border-vino text-vino rounded-full px-6 py-3 text-sm font-medium flex items-center gap-2">
                <Send className="w-4 h-4" /> Enviar notificación de prueba
              </button>
            </div>
            <button onClick={save} data-testid="cfg-notifications-save" className="w-full justify-center bg-vino text-crema rounded-full px-6 py-3 text-sm flex items-center gap-2"><Bell className="w-4 h-4" /> Guardar notificaciones</button>
          </ConfigCard>
        </div>

        <ConfigCard eyebrow="Página pública" title="Banners destacados" description="Controla el banner grande del inicio: texto, imagen, precio, producto y orden."
          className="min-w-0 xl:sticky xl:top-24" open={openSections.public} onToggle={() => toggleSection("public")}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 -mt-1">
            <p className="text-xs text-cafe/50">{banners.length} banner{banners.length === 1 ? "" : "s"} configurado{banners.length === 1 ? "" : "s"}</p>
            <button type="button" onClick={addBanner} data-testid="cfg-banner-add"
              className="w-full sm:w-auto justify-center bg-vino text-crema rounded-full px-5 py-2.5 text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" /> Agregar banner
            </button>
          </div>

        {banners.length === 0 && (
          <div className="bg-crema border border-beige rounded-2xl p-4 text-sm text-cafe/60">
            Aún no has creado banners. Mientras tanto, la app muestra automáticamente un producto destacado.
          </div>
        )}

        <div className="space-y-3 xl:max-h-[calc(100vh-19rem)] xl:overflow-y-auto xl:pr-1">
          {banners.map((b, idx) => (
            <motion.div layout key={b.id || idx} className="bg-crema border border-beige rounded-[22px] overflow-hidden shadow-[0_10px_28px_rgba(118,27,56,0.045)]">
              <button type="button" onClick={() => setOpenBanner(openBanner === idx ? null : idx)}
                className="w-full p-3 sm:p-4 flex items-center justify-between gap-3 text-left hover:bg-blancoCalido/60 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <GripVertical className="w-4 h-4 text-cafe/30 shrink-0" />
                  <div className="w-14 h-11 sm:w-16 sm:h-12 rounded-xl bg-vinoDark/10 overflow-hidden shrink-0">
                    {b.image ? <img src={b.image} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-vino truncate">#{idx + 1} · {b.title || "Banner sin título"}</p>
                    <p className="text-xs text-cafe/50 truncate">{b.eyebrow || "Sin etiqueta"} · {b.enabled === false ? "Inactivo" : "Activo"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button type="button" aria-label="Subir banner" onClick={() => moveBanner(idx, -1)} disabled={idx === 0} className="p-2 rounded-full border border-beige text-vino disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                  <button type="button" aria-label="Bajar banner" onClick={() => moveBanner(idx, 1)} disabled={idx === banners.length - 1} className="p-2 rounded-full border border-beige text-vino disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                  <button type="button" onClick={() => removeBanner(idx)} className="p-2 rounded-full bg-vino/10 text-vino"><Trash2 className="w-4 h-4" /></button>
                  <ChevronDown className={`w-4 h-4 text-cafe/40 transition-transform ${openBanner === idx ? "rotate-180" : ""}`} />
                </div>
              </button>

              <AnimatePresence initial={false}>
              {openBanner === idx && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }}
                className="overflow-hidden">
              <div className="px-3 sm:px-4 pb-4 space-y-4">
                <label className="inline-flex items-center gap-2 text-sm text-vino font-medium">
                  <input type="checkbox" checked={b.enabled !== false} onChange={(e) => setBanner(idx, "enabled", e.target.checked)} />
                  Banner activo
                </label>

                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] 2xl:grid-cols-[minmax(0,1fr)_280px] gap-4 items-start">
                  <div className="min-w-0 space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <label className="text-xs font-medium text-vino mb-1 block">Producto al pedir</label>
                        <select value={b.product_id || ""} onChange={(e) => applyProduct(idx, e.target.value)}
                          className="admin-input bg-blancoCalido">
                          <option value="">Sin producto específico</option>
                          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </div>
                      <BannerInput label="Etiqueta superior" value={b.eyebrow} onChange={(v) => setBanner(idx, "eyebrow", v)} placeholder="Recién horneado" />
                      <BannerInput label="Subtítulo" value={b.subtitle} onChange={(v) => setBanner(idx, "subtitle", v)} placeholder="Lo más rico" />
                      <BannerInput label="Título" value={b.title} onChange={(v) => setBanner(idx, "title", v)} placeholder="Pastel tres leches" />
                      <BannerInput label="Precio / texto destacado" value={b.price_text} onChange={(v) => setBanner(idx, "price_text", v)} placeholder="Desde $420" />
                      <BannerInput label="Texto del botón" value={b.cta_label} onChange={(v) => setBanner(idx, "cta_label", v)} placeholder="Pedir ahora" />
                    </div>

                    <div>
                      <label className="text-xs font-medium text-vino mb-1 block">Imagen del banner</label>
                      <div className="grid grid-cols-1 min-[420px]:grid-cols-[11rem_1fr] gap-3">
                        <div className="h-28 rounded-2xl bg-blancoCalido border border-beige overflow-hidden">
                          {b.image ? <img src={b.image} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <label className="min-h-28 rounded-2xl border-2 border-dashed border-rosa/70 bg-blancoCalido text-vino flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-rosa/10 transition-colors">
                          {uploadingBanner === idx ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                          <span className="text-sm font-medium">Subir foto</span>
                          <span className="text-xs text-cafe/45">JPG, PNG o WebP</span>
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadBannerImage(idx, e.target.files[0])} />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[24px] overflow-hidden border border-beige bg-vino text-crema min-h-[170px] lg:sticky lg:top-28 shadow-pastel">
                    <div className="flex h-full min-h-[170px]">
                      <div className="w-[58%] p-4 flex flex-col justify-center min-w-0">
                        <span className="w-max max-w-full bg-rosa text-vinoDark text-[9px] uppercase tracking-widest px-2.5 py-1 rounded-full truncate">{b.eyebrow || "Etiqueta"}</span>
                        <p className="font-serif italic text-crema/70 text-sm mt-2 line-clamp-2">{b.subtitle || "Subtítulo"}</p>
                        <p className="font-serif text-2xl leading-none line-clamp-3">{b.title || "Título"}</p>
                        <p className="font-serif text-xl text-dorado mt-1 line-clamp-1">{b.price_text || "Precio"}</p>
                      </div>
                      <div className="w-[42%] bg-vinoDark">
                        {b.image ? <img src={b.image} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </motion.div>
              )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <button onClick={save} data-testid="cfg-banner-save" className="w-full justify-center bg-vino text-crema rounded-full px-6 py-3 text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Guardar banners</button>
        </ConfigCard>
      </div>
    </DashboardLayout>
  );
}

const ConfigCard = ({ eyebrow, title, description, compact = false, className = "", open = true, onToggle, children }) => (
  <section className={`bg-blancoCalido border border-beige rounded-[28px] shadow-pastel overflow-hidden ${className}`}>
    <button
      type="button"
      onClick={onToggle}
      className="w-full p-4 sm:p-6 text-left flex items-start justify-between gap-4 hover:bg-crema/45 transition-colors"
    >
      <div className="min-w-0">
        {eyebrow && <p className="text-[11px] uppercase tracking-[0.22em] text-dorado font-semibold mb-1">{eyebrow}</p>}
        <h2 className="font-serif text-2xl sm:text-3xl text-vino leading-tight">{title}</h2>
        {description && <p className="text-sm text-cafe/55 mt-1 max-w-2xl">{description}</p>}
      </div>
      <span className="mt-1 h-10 w-10 shrink-0 rounded-full border border-beige bg-crema text-vino flex items-center justify-center shadow-sm">
        <ChevronDown className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`} />
      </span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="overflow-hidden"
        >
          <div className={`px-4 sm:px-6 pb-4 sm:pb-6 ${compact ? "" : "space-y-4"}`}>{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </section>
);

const ConfigField = ({ label, className = "", children }) => (
  <div className={className}>
    <label className="text-sm font-medium text-vino mb-1.5 block">{label}</label>
    {children}
  </div>
);

const BannerInput = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="text-xs font-medium text-vino mb-1 block">{label}</label>
    <input value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className="admin-input bg-blancoCalido" />
  </div>
);

const NotificationToggle = ({ title, description, checked, onChange, children }) => (
  <div className="rounded-3xl border border-beige bg-crema/70 p-4">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-vino">{title}</p>
        <p className="text-xs text-cafe/55 mt-1">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors shadow-inner ${checked ? "bg-vino" : "bg-beige"}`}
      >
        <span className={`absolute left-1 top-1 h-5 w-5 rounded-full bg-blancoCalido shadow-[0_3px_10px_rgba(47,33,29,0.18)] transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
      </button>
    </div>
    {children && checked && <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>}
  </div>
);
